import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKSPLASH_INPUT_LIMIT, WATERFALL_INPUT_LIMIT } from '@common/constants';
import { NotifierType, showNotification } from '@core/components';

export interface SideItemBoundsNotificationReturn {
    notifyIfOutOfBounds: (test: boolean, limits: LimitsType, textBase: string, enteredValue: number) => void;
}

type OutOfBoundsType = 'MINIMUM' | 'MAXIMUM' | 'ROUND' | undefined;
type LimitsType = typeof WATERFALL_INPUT_LIMIT | typeof BACKSPLASH_INPUT_LIMIT;

export const useSideItemBoundsNotification = (): {
    notifyIfOutOfBounds: (test: boolean, limits: LimitsType, textBase: string, enteredValue: number) => void;
} => {
    const { t } = useTranslation();

    const outOfBoundsType = (limits: LimitsType, enteredValue: number) => {
        if (enteredValue > limits.MAX) {
            return 'MAXIMUM';
        }
        if (enteredValue < limits.MIN) {
            return 'MINIMUM';
        }
        if (enteredValue % limits.ROUND !== 0) {
            return 'ROUND';
        }

        return undefined;
    };
    /**
     * Notify if the entered value is out of bounds, but only if the _test_ value is true
     * @param test if false, the notification will not be triggered
     * @param limits the limit values for the side item type - WATERFALL_INPUT_LIMIT or BACKSPLASH_INPUT_LIMIT
     * @param textBase the base reference for translation.json, must include MIN, MAX, ROUND
     * @param enteredValue
     */
    const notifyIfOutOfBounds = useCallback(
        (test: boolean, limits: LimitsType, textBase: string, enteredValue: number): OutOfBoundsType => {
            if (!test || !enteredValue) {
                return;
            }
            const outOfBounds: OutOfBoundsType = outOfBoundsType(limits, enteredValue);
            if (!outOfBounds) {
                return;
            }
            const title = t(`${textBase}.TITLE`);
            const message = t(`${textBase}.WARNING_${outOfBounds}`, limits);
            if (!message) {
                return;
            }

            showNotification({
                type: NotifierType.WARNING,
                title: title,
                description: message,
                duration: 5,
            });
            return outOfBounds;
        },
        [t]
    );

    return {
        notifyIfOutOfBounds,
    };
};
